(() => {
  const root = document.querySelector('[data-store-locator]');
  if (!root) return;

  const $ = (selector, scope = root) => scope.querySelector(selector);
  const list = $('[data-list]');
  const input = $('[data-search]');
  const filtersEl = $('[data-filters]');
  const countEl = $('[data-count]');
  const emptyEl = $('[data-empty]');
  const locateBtn = $('[data-locate]');
  const locateLabel = $('[data-locate-label]');
  const locateMsg = $('[data-locate-msg]');
  const dialog = $('[data-dialog]');
  const mapEl = $('[data-map]');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const stores = [...root.querySelectorAll('[data-store]')].map((el) => {
    const dishes = el.dataset.dishNames.split('|').filter(Boolean);
    return {
      el,
      id: el.id,
      name: $('.lsl-store__name', el).textContent.trim(),
      state: el.dataset.state,
      brand: el.dataset.brand,
      text: el.dataset.searchText,
      dishes,
      dishText: dishes.join('|').toLowerCase(),
      lat: parseFloat(el.dataset.lat),
      lng: parseFloat(el.dataset.lng),
      kmEl: $('[data-km]', el),
      matchEl: $('[data-match]', el),
      km: Infinity,
    };
  });
  const pinned = (s) => Number.isFinite(s.lat) && Number.isFinite(s.lng);

  const statesByCount = Object.entries(
    stores.reduce((acc, s) => ((acc[s.state] = (acc[s.state] || 0) + 1), acc), {})
  )
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([state]) => state);
  const stateRank = new Map(statesByCount.map((state, i) => [state, i]));
  const defaultOrder = [...stores].sort(
    (a, b) => stateRank.get(a.state) - stateRank.get(b.state) || a.name.localeCompare(b.name)
  );

  // Everything the page shows is derived from this one object by render().
  const view = { query: '', brand: null, origin: null, selectedId: null };

  const tokensOf = (query) => query.toLowerCase().split(/\s+/).filter(Boolean);
  const matchesQuery = (s, tokens) => tokens.every((t) => s.text.includes(t) || s.dishText.includes(t));
  // Words the store's own name, address or state already explain are left out, so "aeon" never claims a dish.
  function dishesMatching(s, tokens) {
    const dishTokens = tokens.filter((t) => !s.text.includes(t));
    if (!dishTokens.length) return [];
    return s.dishes.filter((d) => dishTokens.every((t) => d.toLowerCase().includes(t)));
  }
  const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

  function kmBetween(a, b) {
    const rad = Math.PI / 180;
    const h =
      Math.sin(((b.lat - a.lat) * rad) / 2) ** 2 +
      Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(((b.lng - a.lng) * rad) / 2) ** 2;
    return 12742 * Math.asin(Math.sqrt(h));
  }
  const formatKm = (km) => (km < 10 ? `${km.toFixed(1)} km` : `${Math.round(km)} km`);

  // Every retailer with a logo gets a map flag; only chains with 2+ stores get a filter button, so the row fits.
  const brandCount = (brand) => stores.filter((s) => s.brand === brand).length;
  const brandButtons = [...filtersEl.querySelectorAll('button[data-brand]')]
    .filter((b) => brandCount(b.dataset.brand) > 0)
    .sort((a, b) => brandCount(b.dataset.brand) - brandCount(a.dataset.brand));
  const filterBrands = brandButtons.filter((b) => brandCount(b.dataset.brand) >= 2);
  brandButtons.forEach((button) => {
    filtersEl.append(button);
    button.hidden = !filterBrands.includes(button);
    const logoImg = button.querySelector('img');
    logoImg?.addEventListener('error', () => { button.hidden = true; });
    if (logoImg?.complete && logoImg.currentSrc && !logoImg.naturalWidth) button.hidden = true;
    button.addEventListener('click', () => {
      if (button.getAttribute('aria-disabled') === 'true') return;
      const brand = button.dataset.brand;
      view.brand = view.brand === brand ? null : brand;
      window.dataLayer?.push({ event: 'lsl_retailer_filter', retailer: brand, active: view.brand === brand });
      render({ fit: true });
    });
  });
  const brandName = (brand) => brandButtons.find((b) => b.dataset.brand === brand)?.dataset.name || '';

  function render({ fit = false } = {}) {
    const tokens = tokensOf(view.query);
    const byQuery = stores.filter((s) => matchesQuery(s, tokens));
    const atBrand = (s) => !view.brand || s.brand === view.brand;
    const visible = byQuery.filter(atBrand);
    const ordered = view.origin
      ? [...visible].sort((a, b) => a.km - b.km)
      : defaultOrder.filter((s) => visible.includes(s));

    stores.forEach((s) => {
      s.el.hidden = true;
      s.kmEl.hidden = !view.origin || !pinned(s);
      if (view.origin && pinned(s)) s.kmEl.textContent = formatKm(s.km);
      const matched = dishesMatching(s, tokens);
      s.matchEl.hidden = !matched.length;
      s.matchEl.textContent = matched.length ? `Carries ${matched.join(', ')}` : '';
    });
    ordered.forEach((s) => {
      s.el.hidden = false;
      list.append(s.el);
    });
    if (fit) list.scrollTop = 0;

    filterBrands.forEach((button) => {
      const brand = button.dataset.brand;
      const label = `${button.dataset.name}, ${plural(byQuery.filter((s) => s.brand === brand).length, 'store')}`;
      button.setAttribute('aria-label', label);
      button.title = label;
      button.setAttribute('aria-pressed', String(view.brand === brand));
      // aria-disabled, not disabled: the button keeps keyboard focus when it greys out right after being cleared.
      button.setAttribute('aria-disabled', String(view.brand !== brand && !byQuery.some((s) => s.brand === brand)));
    });

    const where = view.brand ? ` at ${brandName(view.brand)}` : '';
    const filtered = tokens.length > 0 || view.brand;
    let summary = filtered
      ? plural(ordered.length, 'store') + where + (tokens.length ? ` for "${view.query.trim()}"` : '')
      : `${plural(stores.length, 'store')} across ${plural(statesByCount.length, 'state')}`;
    if (view.origin && ordered.length) summary += ', nearest first';
    countEl.textContent = summary;

    emptyEl.hidden = ordered.length > 0;
    list.hidden = ordered.length === 0;
    if (!ordered.length) {
      $('[data-empty-title]').textContent = tokens.length
        ? `No store matches "${view.query.trim()}"${where}.`
        : `No stores${where}.`;
    }

    if (view.selectedId && !ordered.some((s) => s.id === view.selectedId)) select(null);
    mapView.update(ordered, fit);
  }

  let searchTimer;
  input.addEventListener('input', () => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      view.query = input.value;
      render({ fit: true });
    }, 150);
  });
  $('[data-search-form]').addEventListener('submit', (event) => {
    event.preventDefault();
    clearTimeout(searchTimer);
    view.query = input.value;
    render({ fit: true });
    input.blur();
  });
  $('[data-clear]').addEventListener('click', () => {
    input.value = '';
    view.query = '';
    view.brand = null;
    render({ fit: true });
    input.focus();
  });

  function setLocate(state, message = '') {
    locateBtn.setAttribute('aria-pressed', String(state === 'on'));
    locateBtn.disabled = state === 'finding';
    locateLabel.textContent = state === 'finding' ? 'Finding you' : 'Near me';
    locateMsg.hidden = !message;
    locateMsg.textContent = message;
  }

  locateBtn.addEventListener('click', () => {
    if (view.origin) {
      view.origin = null;
      setLocate('off');
      mapView.showOrigin(null);
      render({ fit: true });
      return;
    }
    if (!('geolocation' in navigator)) {
      setLocate('off', "This browser can't share your location. Search your area instead.");
      return;
    }
    setLocate('finding');
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        view.origin = { lat: coords.latitude, lng: coords.longitude };
        stores.forEach((s) => (s.km = pinned(s) ? kmBetween(view.origin, s) : Infinity));
        setLocate('on');
        mapView.showOrigin(view.origin);
        render();
        list.scrollTop = 0;
        mapView.fitNearest(view.origin, list.querySelectorAll('[data-store]:not([hidden])'));
      },
      (error) => {
        setLocate(
          'off',
          error.code === error.PERMISSION_DENIED
            ? 'Location is off for this site. Search your area instead.'
            : "We couldn't find your location. Search your area instead."
        );
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  });

  function select(id, { from } = {}) {
    view.selectedId = id;
    stores.forEach((s) => s.el.classList.toggle('is-selected', s.id === id));
    mapView.select(id);
    if (!id || from !== 'map') return;
    const store = stores.find((s) => s.id === id);
    if (list.scrollHeight > list.clientHeight + 1) revealCard(store);
  }

  // Bring a store card to the top of whatever scrolls: the list column on desktop, the page (below the sticky header) on phones.
  function revealCard(store) {
    const behavior = reduceMotion ? 'auto' : 'smooth';
    if (list.scrollHeight > list.clientHeight + 1) {
      list.scrollTo({ top: list.scrollTop + store.el.getBoundingClientRect().top - list.getBoundingClientRect().top - 8, behavior });
      return;
    }
    const headerHeight = document.querySelector('.section-header')?.offsetHeight || 0;
    window.scrollTo({ top: window.scrollY + store.el.getBoundingClientRect().top - headerHeight - 12, behavior });
  }

  list.addEventListener('click', (event) => {
    const storeEl = event.target.closest('[data-store]');
    if (!storeEl) return;
    if (event.target.closest('[data-show-on-map]')) {
      select(storeEl.id, { from: 'list' });
      mapView.focus(stores.find((s) => s.id === storeEl.id));
      const rect = mapEl.getBoundingClientRect();
      // The sticky header slides back in when the page scrolls up, so always leave room for its full height.
      const headerHeight = document.querySelector('.section-header')?.offsetHeight || 0;
      if (rect.top < headerHeight || rect.bottom > window.innerHeight) {
        window.scrollTo({ top: window.scrollY + rect.top - headerHeight - 12, behavior: reduceMotion ? 'auto' : 'smooth' });
      }
    } else if (event.target.closest('[data-open-dishes]')) {
      openDishes(storeEl);
    }
  });

  function openDishes(storeEl) {
    const key = $('[data-open-dishes]', storeEl).dataset.openDishes;
    $('[data-dialog-title]').textContent = $('.lsl-store__name', storeEl).textContent;
    const body = $('[data-dialog-body]');
    body.replaceChildren($(`template[data-dishes="${CSS.escape(key)}"]`).content.cloneNode(true));
    body.scrollTop = 0;
    document.body.style.overflow = 'hidden';
    dialog.showModal();
  }
  dialog.addEventListener('close', () => {
    document.body.style.overflow = '';
  });
  $('[data-dialog-close]').addEventListener('click', () => dialog.close());
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });

  const mapView = createMap();

  function createMap() {
    const lngs = stores.filter(pinned).map((s) => s.lng);
    const lats = stores.filter(pinned).map((s) => s.lat);
    const allStores = lngs.length
      ? [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]]
      : [[99.6, 1.2], [104.6, 6.8]];
    const GOLD = '#BE995C';
    const GOLD_DARK = '#7E5F2C';
    let map = null;
    let ready = false;
    let pending = null;
    let popup = null;
    let originMarker = null;
    let lastItems = [];
    const flagReady = new Set();
    const animate = (options) => (reduceMotion ? { ...options, duration: 0 } : options);

    const fail = () => {
      if (map) map.remove();
      map = null;
      root.classList.add('lsl--no-map');
      $('[data-map-fallback]').hidden = false;
    };

    const toFeatures = (items) => ({
      type: 'FeatureCollection',
      features: items.filter(pinned).map((s) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [s.lng, s.lat] },
        properties: flagReady.has(s.brand) ? { id: s.id, flag: `flag-${s.brand}` } : { id: s.id },
      })),
    });

    function fitTo(items) {
      const points = items.filter(pinned);
      if (!points.length) return;
      if (points.length === 1) {
        map.easeTo(animate({ center: [points[0].lng, points[0].lat], zoom: 14 }));
        return;
      }
      const bounds = new window.maplibregl.LngLatBounds();
      points.forEach((s) => bounds.extend([s.lng, s.lat]));
      // Extra room at the top and right: flags stand above their pin, and the zoom buttons sit top right.
      map.fitBounds(bounds, animate({ padding: { top: 72, right: 56, bottom: 28, left: 28 }, maxZoom: 13 }));
    }

    function closePopup() {
      const old = popup;
      popup = null;
      if (old) old.remove();
    }

    // The flag stands up to 58px above its pin (1.25x when selected), so lift the pop-up only on the side the flag occupies.
    const FLAG_OFFSETS = {
      center: [0, -30], bottom: [0, -60], 'bottom-left': [0, -60], 'bottom-right': [0, -60],
      top: [0, 8], 'top-left': [8, 8], 'top-right': [-8, 8], left: [44, -30], right: [-44, -30],
    };
    function openPopup(store) {
      closePopup();
      const box = document.createElement('div');
      box.className = 'lsl-pop';
      const name = Object.assign(document.createElement('p'), { className: 'lsl-pop__name', textContent: store.name });
      const actions = document.createElement('p');
      actions.className = 'lsl-pop__actions';
      const viewLink = Object.assign(document.createElement('a'), { href: `#${store.id}`, textContent: 'View store' });
      viewLink.addEventListener('click', (event) => {
        event.preventDefault();
        revealCard(store);
        store.el.querySelector('button, a')?.focus({ preventScroll: true });
      });
      const directions = Object.assign(document.createElement('a'), {
        href: `https://www.google.com/maps/dir/?api=1&destination=${store.lat},${store.lng}`,
        target: '_blank',
        rel: 'noopener',
        textContent: 'Directions',
      });
      actions.append(viewLink, directions);
      box.append(name, actions);
      const mine = new window.maplibregl.Popup({ offset: flagReady.has(store.brand) ? FLAG_OFFSETS : 14, closeButton: true, maxWidth: '260px' })
        .setLngLat([store.lng, store.lat])
        .setDOMContent(box)
        .addTo(map);
      popup = mine;
      mine.on('close', () => {
        if (popup !== mine) return;
        popup = null;
        if (view.selectedId === store.id) select(null);
      });
    }

    if (!window.maplibregl) {
      fail();
    } else {
      try {
        map = new window.maplibregl.Map({
          container: mapEl,
          style: root.dataset.styleUrl,
          bounds: allStores,
          fitBoundsOptions: { padding: 32 },
          attributionControl: { compact: true },
          cooperativeGestures: true,
          dragRotate: false,
          pitchWithRotate: false,
        });
        map.touchZoomRotate.disableRotation();
        map.addControl(new window.maplibregl.NavigationControl({ showCompass: false }), 'top-right');
      } catch (error) {
        fail();
      }
    }

    if (map) {
      map.on('error', () => {
        if (!ready) fail();
      });
      map.on('load', () => {
        ready = true;
        mapEl.querySelector('.maplibregl-ctrl-attrib')?.classList.remove('maplibregl-compact-show');
        // Warm the grey basemap toward the site palette; skip quietly if a different style lacks these layers.
        [['background', 'background-color', '#F7F5F1'], ['water', 'fill-color', '#D6E3E8']].forEach(([layer, prop, value]) => {
          if (map.getLayer(layer)) map.setPaintProperty(layer, prop, value);
        });
        map.addSource('stores', {
          type: 'geojson',
          data: toFeatures([]),
          cluster: true,
          clusterRadius: 42,
          clusterMaxZoom: 12,
        });
        map.addLayer({
          id: 'clusters',
          type: 'circle',
          source: 'stores',
          filter: ['has', 'point_count'],
          paint: {
            'circle-color': GOLD,
            'circle-radius': ['step', ['get', 'point_count'], 16, 10, 20, 25, 24],
            'circle-stroke-width': 0,
          },
        });
        map.addLayer({
          id: 'cluster-count',
          type: 'symbol',
          source: 'stores',
          filter: ['has', 'point_count'],
          layout: { 'text-field': ['get', 'point_count_abbreviated'], 'text-font': ['Noto Sans Bold'], 'text-size': 13, 'text-allow-overlap': true, 'text-ignore-placement': true },
          paint: { 'text-color': '#FFFFFF' },
        });
        map.addLayer({
          id: 'points',
          type: 'circle',
          source: 'stores',
          filter: ['all', ['!', ['has', 'point_count']], ['!', ['has', 'flag']]],
          paint: { 'circle-color': GOLD, 'circle-radius': 8, 'circle-stroke-width': 0 },
        });
        map.addLayer({
          id: 'selected',
          type: 'circle',
          source: 'stores',
          filter: ['==', ['get', 'id'], ''],
          paint: { 'circle-color': GOLD_DARK, 'circle-radius': 11, 'circle-stroke-width': 0 },
        });
        map.addLayer({
          id: 'flags',
          type: 'symbol',
          source: 'stores',
          filter: ['all', ['!', ['has', 'point_count']], ['has', 'flag']],
          layout: { 'icon-image': ['get', 'flag'], 'icon-anchor': 'bottom', 'icon-allow-overlap': true, 'icon-size': 1 },
        });
        loadFlags();

        map.on('click', 'clusters', async (event) => {
          const feature = event.features[0];
          const zoom = await map.getSource('stores').getClusterExpansionZoom(feature.properties.cluster_id);
          // At least two levels in: the first split can peel off a few far-out pins and leave the big group whole.
          map.easeTo(animate({ center: feature.geometry.coordinates, zoom: Math.max(zoom, map.getZoom() + 2) }));
        });
        ['points', 'flags'].forEach((layer) => {
          map.on('click', layer, (event) => select(event.features[0].properties.id, { from: 'map' }));
        });
        ['clusters', 'points', 'flags'].forEach((layer) => {
          map.on('mouseenter', layer, () => (map.getCanvas().style.cursor = 'pointer'));
          map.on('mouseleave', layer, () => (map.getCanvas().style.cursor = ''));
        });

        if (pending) {
          const { items, fit } = pending;
          pending = null;
          api.update(items, fit);
        }
        if (view.selectedId) {
          api.select(view.selectedId);
          api.focus(stores.find((s) => s.id === view.selectedId));
        }
      });
    }

    // A retailer's map pin is its logo card on a small flag pole; a logo that fails to load leaves the gold dot.
    function loadFlags() {
      brandButtons.filter((b) => b.dataset.flag).forEach(async (button) => {
        try {
          const { data } = await map.loadImage(button.dataset.flag);
          map.addImage(`flag-${button.dataset.brand}`, drawFlag(data), { pixelRatio: 2 });
          flagReady.add(button.dataset.brand);
          map.getSource('stores').setData(toFeatures(lastItems));
        } catch (error) {
          // Logo unavailable: these stores keep the plain gold dot.
        }
      });
    }

    function drawFlag(logo) {
      const w = 132, cardH = 72, h = 92;
      const canvas = Object.assign(document.createElement('canvas'), { width: w, height: h });
      const g = canvas.getContext('2d');
      g.shadowColor = 'rgba(44, 34, 24, 0.28)';
      g.shadowBlur = 6;
      g.shadowOffsetY = 2;
      g.fillStyle = '#FFFFFF';
      g.beginPath();
      g.roundRect(4, 2, w - 8, cardH - 6, 12);
      g.moveTo(w / 2 - 9, cardH - 5);
      g.lineTo(w / 2, h - 6);
      g.lineTo(w / 2 + 9, cardH - 5);
      g.fill();
      g.shadowColor = 'transparent';
      const boxW = w - 8, boxH = cardH - 6;
      const scale = Math.min(boxW / logo.width, boxH / logo.height);
      g.drawImage(logo, 4 + (boxW - logo.width * scale) / 2, 2 + (boxH - logo.height * scale) / 2, logo.width * scale, logo.height * scale);
      return g.getImageData(0, 0, w, h);
    }

    const api = {
      update(items, fit) {
        lastItems = items;
        if (!map) return;
        if (!ready) {
          pending = { items, fit };
          return;
        }
        map.getSource('stores').setData(toFeatures(items));
        if (fit) fitTo(items);
      },
      select(id) {
        if (!map || !ready) return;
        // A store with a logo flag is marked by enlarging its flag, so it gets no dark dot underneath.
        map.setFilter('selected', ['all', ['==', ['get', 'id'], id || ''], ['!', ['has', 'flag']]]);
        map.setLayoutProperty('flags', 'icon-size', ['case', ['==', ['get', 'id'], id || ''], 1.25, 1]);
        const store = stores.find((s) => s.id === id);
        if (store && pinned(store)) openPopup(store);
        else closePopup();
      },
      focus(store) {
        if (!map || !ready || !pinned(store)) return;
        map.flyTo(animate({ center: [store.lng, store.lat], zoom: Math.max(map.getZoom(), 14) }));
      },
      showOrigin(origin) {
        if (!map) return;
        if (originMarker) originMarker.remove();
        originMarker = null;
        if (!origin) return;
        const dot = Object.assign(document.createElement('span'), { className: 'lsl-you' });
        dot.setAttribute('aria-label', 'You are here');
        originMarker = new window.maplibregl.Marker({ element: dot }).setLngLat([origin.lng, origin.lat]).addTo(map);
      },
      fitNearest(origin, cards) {
        if (!map || !ready) return;
        const nearest = [...cards].slice(0, 3).map((el) => stores.find((s) => s.id === el.id)).filter(pinned);
        fitTo([...nearest, { lat: origin.lat, lng: origin.lng }]);
      },
    };
    return api;
  }

  render();

  const linked = stores.find((s) => `#${s.id}` === window.location.hash);
  if (linked) {
    select(linked.id);
    mapView.focus(linked);
    linked.el.scrollIntoView({ block: 'center' });
  }
})();
